import { ParsedData, InferenceStats } from '../types';

export function calculateInferenceStats(times: number[]): InferenceStats | undefined {
  if (times.length === 0) {
    return undefined;
  }

  const sorted = [...times].sort((a, b) => a - b);
  const sum = sorted.reduce((acc, val) => acc + val, 0);

  const percentile = (arr: number[], p: number) => {
    const index = Math.ceil(arr.length * p) - 1;
    return arr[Math.max(0, index)];
  };

  return {
    average: sum / sorted.length,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    median: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    p99: percentile(sorted, 0.99),
    total: sum,
    count: sorted.length
  };
}

export function getModelInferenceStats(data: ParsedData, modelName: string): InferenceStats | undefined {
  const times: number[] = [];

  data.examples.forEach(example => {
    const response = example.modelResponses.find(r => r.modelName === modelName);
    if (response?.inferenceTime !== undefined) {
      times.push(response.inferenceTime);
    }
  });

  return calculateInferenceStats(times);
}

export function getAllModelsInferenceStats(data: ParsedData): Map<string, InferenceStats | undefined> {
  const statsMap = new Map<string, InferenceStats | undefined>();

  data.modelNames.forEach(modelName => {
    statsMap.set(modelName, getModelInferenceStats(data, modelName));
  });

  return statsMap;
}

export function formatTime(seconds: number): string {
  if (seconds < 0.001) {
    return `${(seconds * 1000000).toFixed(0)}µs`;
  } else if (seconds < 1) {
    return `${(seconds * 1000).toFixed(2)}ms`;
  } else {
    return `${seconds.toFixed(3)}s`;
  }
}

export function getModelConfidenceStats(data: ParsedData, modelName: string): InferenceStats | undefined {
  const scores: number[] = [];

  data.examples.forEach(example => {
    const response = example.modelResponses.find(r => r.modelName === modelName);
    if (response) {
      response.entities.forEach(entity => {
        if (entity.confidence !== undefined) {
          scores.push(entity.confidence);
        }
      });
    }
  });

  return calculateInferenceStats(scores);
}

export function formatConfidence(score: number): string {
  return `${(score * 100).toFixed(1)}%`;
}
